import { Box, Card, Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLoadCVParsedStatusCount,
  ICVUploadAnalytics,
  getCVParserStatusAdmin,
  getCVParsedTotalProcessedCountAnalytics,
} from 'wisestep-api';
import {
  WsTitleBanner,
  getCurrentZoneAbbr,
  WsColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { getSumOfColumnRecords } from '../../../features/composableAnalytics/BaseCharts/clientListing/helper/payloadsAndTypesHelper';
import CVParserStatusByWorkspace from '../cvParser/components/CVParserStatusByWorkspace';
import CVParserStatusCount from '../cvParser/components/CVParserStatusCount';
import CVParsedRecord from '../cvupload/component/CVParsedRecord';
import { subtractYears } from '../cvupload/CVUploadAnalytics';

interface ICVParsedcountType {
  parsedCount: string;
  type: string;
  hour: string;
  status: string;
}

export interface ICVParserStatusAdmin {
  tableData: Record<string, unknown>[];
  status: string;
  type: string;
  hour: string;
}

export default function CVParserAnalytics() {
  const navigate = useNavigate();
  const [cvParserDataAdmin, setCvParserDataAdmin] = useState<ICVParserStatusAdmin[]>([]);
  const [cvParserData, setCvParserData] = useState<ICVParsedcountType[]>([]);
  const [cvParserRecordData, setCvParserRecordData] = useState<ICVParsedcountType[]>([]);

  const typeArray = useMemo(
    () => [
      { hour: '1', type: 'h' },
      { hour: '4', type: 'h' },
      { hour: '8', type: 'h' },
      { hour: '24', type: 'h' },
      { hour: '7', type: 'd' },
      { hour: '30', type: 'd' },
      { hour: '60', type: 'd' },
      { hour: '90', type: 'd' },
    ],
    []
  );
  const processingArray = useMemo(
    () => [
      { hour: '1', type: 'h' },
      { hour: '8', type: 'h' },
      { hour: '24', type: 'h' },
      { hour: '7', type: 'd' },
      { hour: '30', type: 'd' },
      { hour: '60', type: 'd' },
    ],
    []
  );

  const successArray = useMemo(
    () => [
      { hour: '7', type: 'd' },
      { hour: '30', type: 'd' },
      { hour: '60', type: 'd' },
      { hour: '90', type: 'd' },
    ],
    []
  );

  const statusArray = useMemo(() => ['SUCCESS', 'PROCESSING'], []);

  useEffect(() => {
    const mapStatusArray = (array, statusArray) => {
      const payload: ICVUploadAnalytics[] = [];
      statusArray.map((status) => {
        payload.push({
          timeDifference: array.hour,
          type: array.type,
          status: status,
        });
      });
      return payload;
    };
    const cvParsedStatusCount = async () => {
      const payload: ICVUploadAnalytics[] = [];
      typeArray.map((array) => {
        const mappedStatusArray = mapStatusArray(array, statusArray);
        payload.push(...mappedStatusArray);
      });
      const response = await getLoadCVParsedStatusCount(payload);
      setCvParserData(response.data);
    };
    const cvParserStatusAdmin = async () => {
      const payload: ICVUploadAnalytics[] = [];
      successArray.map((array) => {
        const mappedStatusArray = mapStatusArray(array, statusArray);
        payload.push(...mappedStatusArray);
      });

      const response = await getCVParserStatusAdmin(payload);
      setCvParserDataAdmin(response.data);
    };

    const cvParsedStatusCounts = async () => {
      const payload: ICVUploadAnalytics[] = [];
      processingArray.map((array) => {
        payload.push({
          timeDifference: array.hour,
          type: array.type,
        });
      });
      const response = await getCVParsedTotalProcessedCountAnalytics(payload);
      setCvParserRecordData(response.data);
    };

    cvParsedStatusCount();
    cvParserStatusAdmin();
    cvParsedStatusCounts();
  }, [successArray, statusArray, typeArray, processingArray]);

  const columns: GridColDef[] = [
    { field: 'workspaceName', headerName: `Workspace Name`, flex: 2 },
    {
      field: 'parsedCount',
      headerName: `Total Count`,
      flex: 1,
    },
  ];

  const filterQuery: ICVUploadAnalytics = {
    utcOffset: getCurrentZoneAbbr(),
    timeDifference: '1',
    utcOffsetInHour: 5,
    utcOffsetInMinute: 30,
  };
  const date = new Date();
  filterQuery.fromDate = subtractYears(1);
  filterQuery.toDate = date;

  return (
    <Grid>
      <WsTitleBanner title="CV Parser Analytics" goBack={() => navigate('/analytics/')} />
      <Grid p={2} bgcolor={WsColors.White} pt={4}>
        {cvParserData && (
          <Grid item xs={12}>
            <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
              Dashboard
            </WsTypography>
          </Grid>
        )}
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {cvParserData.map((item, index) => (
            <Grid item xs={3} key={index}>
              <CVParserStatusCount
                count={item.parsedCount}
                hour={item.hour}
                status={item.status}
                type={item.type}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} pb={4} mt={4}>
          {cvParserRecordData && (
            <Grid item xs={12}>
              <Card sx={{ minHeight: 40, p: 0.5 }}>
                <Box textAlign="start">
                  <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                    CVs Upload Volumes
                  </WsTypography>
                </Box>
              </Card>
            </Grid>
          )}
          {cvParserRecordData.map((item, index) => (
            <Grid item xs={2} key={index}>
              <CVParsedRecord
                count={item.parsedCount}
                hour={item.hour}
                type={item.type}
                titleText={''}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} pt={4}>
          {cvParserDataAdmin && (
            <Grid item xs={12} pb={2}>
              <Card sx={{ minHeight: 80, m: 2 }}>
                <Box textAlign="center" mt={5}>
                  <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                    Workspace Level Detailed Reports
                  </WsTypography>
                </Box>
              </Card>
            </Grid>
          )}
          {cvParserDataAdmin.map((item, index: number) => (
            <Grid item xs={4} key={index}>
              <CVParserStatusByWorkspace
                columns={getSumOfColumnRecords(item.tableData, columns, ['workspaceName'])}
                rows={item.tableData}
                hour={item.hour}
                status={item.status}
                type={item.type}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}
